/**
 * Hellovedic Hair Assessment Application Engine
 * Pure Vanilla JS, Single Page Data-Driven Architecture
 * Production-Ready Dynamic Appointment Booking & TeleCRM Lead API Integration with Full Q&A & Images Payload.
 * Includes Facebook Pixel Event Tracking Integration.
 */

// ==========================================================================
// 1. Application State & Storage Management
// ==========================================================================

const STORAGE_KEY = "hellovedic_hair_assessment_state";

const state = {
  language: "ml",
  currentStep: 0,
  answers: { stress_level: null },
  booking: null, // Holds active booking object { dateISO, dateLabel, dateDisplay, period, slot, slotEndTime }
  transitionDirection: "next" // "next", "prev", or "fade"
};

const urlParams = new URLSearchParams(window.location.search);
const fbclid = urlParams.get('fbclid');

let fbc = null;

// Check if Meta Pixel already set the _fbc cookie
const fbcCookie = document.cookie.split('; ').find(c => c.startsWith('_fbc='));
if (fbcCookie) {
  fbc = fbcCookie.split('=')[1];
} else if (fbclid) {
  fbc = `fb.1.${Date.now()}.${fbclid}`;
}

if (fbc) {
  sessionStorage.setItem('fbc', fbc);
}

/**
 * Save current state to localStorage
 */
function saveState() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      language: state.language,
      currentStep: state.currentStep,
      answers: state.answers,
      booking: state.booking
    }));
  } catch (e) {
    console.warn("LocalStorage save failed:", e);
  }
}

/**
 * Load saved state from localStorage if available
 */
function loadSavedState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.language && uiTranslations[parsed.language]) {
        state.language = parsed.language;
      }
      const qList = getQuestionsList();
      const loadedStep = typeof parsed.currentStep === "number" ? parsed.currentStep : 0;
      const targetQ = qList[loadedStep];

      // If saved state is on completion screen or invalid, reset to first page (Step 0)
      if (!targetQ || targetQ.type === "success" || targetQ.id === "assessment_result" || loadedStep >= qList.length - 1) {
        state.currentStep = 0;
        state.answers = {};
        state.booking = null;
        localStorage.removeItem(STORAGE_KEY);
      } else {
        state.currentStep = loadedStep;
        if (parsed.answers && typeof parsed.answers === "object") {
          state.answers = parsed.answers;
        }
        if (parsed.booking && typeof parsed.booking === "object") {
          state.booking = parsed.booking;
        }
      }
    }
  } catch (e) {
    console.warn("LocalStorage load failed:", e);
  }
}

// ==========================================================================
// 2. Configurable Booking System & Time Utilities Architecture
// ==========================================================================

const SLOT_CONFIG = {
  durationMinutes: 30,
  bookingBufferMinutes: 30,
  numAvailableDays: 2, // Today and Tomorrow Only
  periods: {
    morning: ["10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM"],
    afternoon: ["02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM"],
    evening: ["05:00 PM", "05:30 PM", "06:00 PM", "06:30 PM", "07:00 PM"]
  }
};

/**
 * Availability Manager API Layer
 */
const AvailabilityManager = {
  overrideMap: {},

  setAvailabilityData(apiData) {
    if (apiData && typeof apiData === "object") {
      this.overrideMap = apiData;
    }
  },

  isSlotAvailable(dateISO, slotStr) {
    if (this.overrideMap[dateISO] && this.overrideMap[dateISO][slotStr] !== undefined) {
      return Boolean(this.overrideMap[dateISO][slotStr]);
    }
    return true;
  }
};

/**
 * Time Utility: Format TeleCRM Date (e.g. "05/08/2026 10:00")
 */
function formatTeleCRMDate(dateISO, timeStr) {
  if (!dateISO || !timeStr) return "";
  const [year, month, day] = dateISO.split("-");
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return "";

  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const period = match[3].toUpperCase();

  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;

  return `${day}/${month}/${year} ${String(hour).padStart(2, "0")}:${minute}`;
}

/**
 * Time Utility: Format Display Date (e.g. "5th August, 10:00 AM")
 */
function formatDisplayDate(dateStr) {
  if (!dateStr) return "";
  const [datePart, timePart] = dateStr.split(" ");
  if (!datePart || !timePart) return dateStr;
  const [day, month, year] = datePart.split("/").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  const date = new Date(year, month - 1, day, hour, minute);

  const ordinal = (n) => {
    if (n > 3 && n < 21) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };

  const dayWithOrdinal = ordinal(date.getDate());
  const monthName = date.toLocaleString("en-US", { month: "long" });
  const time = date.toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${dayWithOrdinal} ${monthName}, ${time}`;
}

/**
 * TeleCRM API Lead Creation on Name & Phone submission (Includes WhatsApp & Calling Contact Numbers)
 */
async function sendTeleCRMCreateLead() {
  const userName = state.answers["name"] || "Customer";
  let rawPhone = String(state.answers["phone"] || "").replace(/\D/g, "").slice(-10);
  if (!rawPhone || rawPhone.length !== 10) return;

  let callingPhone = String(state.answers["calling_phone"] || rawPhone).replace(/\D/g, "").slice(-10);
  if (!callingPhone || callingPhone.length !== 10) callingPhone = rawPhone;

  const gender = state.answers["gender"]


  const fields = {
    name: userName,
    phone: `91${rawPhone}`,
    gender: gender,
    last_action_by: "New Lead Created"
  };


  if (callingPhone && callingPhone !== rawPhone) {
    fields.calling_phone = callingPhone;
    fields.secondary_phone = `91${callingPhone}`;
  }

  if (typeof fbc !== "undefined" && fbc) fields.fbc = fbc;
  if (typeof fbclid !== "undefined" && fbclid) fields.fbclid = fbclid;

  const payload = {
    fields: fields,
    tags: ["Hair Assessment Lead"]
  };

  try {
    console.log("Creating TeleCRM Lead on Name/Phone submission:", payload);
    const response = await fetch("https://next-api.telecrm.in/enterprise/64269d9e5a2c3a00081251ec/autoupdatelead", {
      method: "POST",
      headers: {
        "Authorization": "Bearer 254daded-32f8-4772-9e09-a451ef92a6641763550822427:f1015b9b-f10c-4375-b950-1f374c0ea940",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload),
      keepalive: true
    });
    const result = await response.json().catch(() => ({}));
    console.log("TeleCRM Create Lead Response:", result);
    return result;
  } catch (error) {
    console.error("TeleCRM Create Lead Error:", error);
  }
}

/**
 * TeleCRM API Lead Auto-Update Submission (Includes ALL Q&A, Dual Scalp Photos & Calling/Alt Phones)
 */
async function sendTeleCRMLeadUpdate() {
  let rawPhone = String(state.answers["phone"] || "").replace(/\D/g, "").slice(-10);
  if (!rawPhone || rawPhone.length !== 10) return;

  const primaryPhone = "91" + rawPhone;
  let callingPhone = String(state.answers["calling_phone"] || rawPhone).replace(/\D/g, "").slice(-10);
  const altPhone = String(state.answers["alt_phone"] || "").replace(/\D/g, "").slice(-10);
  const age = state.answers["age"] || ""

  const gender = state.answers["genter"] || ""

  const fields = {
    phone: primaryPhone,
    name: state.answers["name"] || "",
    last_action_by: "Form Submitted"
  };

  if (callingPhone && callingPhone.length === 10 && callingPhone !== rawPhone) {
    fields.calling_phone = callingPhone;
    fields.secondary_phone = "91" + callingPhone;
  }

  if (altPhone && altPhone.length === 10) {
    fields.alternate_phone = "91" + altPhone;
  }

  if (typeof fbc !== "undefined" && fbc) fields.fbc = fbc;
  if (typeof fbclid !== "undefined" && fbclid) fields.fbclid = fbclid;


  if(gender) {
    fields.gender = gender
  }

 if (age) {
  if (age < 18) {
    fields.ages = "under 18";
  } else if (age <= 24) {
    fields.ages = "18-24";
  } else if (age <= 34) {
    fields.ages = "25-34";
  } else if (age <= 44) {
    fields.ages = "35-44";
  } else if (age <= 49) {
    fields.ages = "45-49";
  } else {
    fields.ages = "50 above";
  }
}

  const payload = { fields };

  try {
    console.log("Submitting Full Assessment TeleCRM Lead Payload:", payload);
    const response = await fetch("https://next-api.telecrm.in/enterprise/64269d9e5a2c3a00081251ec/autoupdatelead", {
      method: "POST",
      headers: {
        "Authorization": "Bearer 254daded-32f8-4772-9e09-a451ef92a6641763550822427:f1015b9b-f10c-4375-b950-1f374c0ea940",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload),
      keepalive: true
    });
    const result = await response.json().catch(() => ({}));
    console.log("TeleCRM API Response:", result);
    return result;
  } catch (error) {
    console.error("TeleCRM API Submission Error:", error);
  }
}

/**
 * Time Utility: Parse "10:30 AM" to minutes from midnight
 */
function parseTime(timeStr) {
  if (!timeStr) return 0;
  const match = String(timeStr).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return 0;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === "PM" && hours < 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

/**
 * Time Utility: Format minutes from midnight to "10:30 AM"
 */
function formatTime(minutes) {
  let hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  const period = hours >= 12 ? "PM" : "AM";
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  const minsStr = mins < 10 ? `0${mins}` : `${mins}`;
  return `${hours}:${minsStr} ${period}`;
}

/**
 * Time Utility: Calculate End Time automatically
 */
function calculateEndTime(startTimeStr, durationMinutes = SLOT_CONFIG.durationMinutes) {
  const startMins = parseTime(startTimeStr);
  const endMins = startMins + durationMinutes;
  return formatTime(endMins);
}

/**
 * Time Utility: Check if slot is past for Same-Day Booking (with buffer)
 */
function isPastSlot(timeStr, dateISO, bufferMinutes = SLOT_CONFIG.bookingBufferMinutes) {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const todayISO = `${year}-${month}-${day}`;

  if (dateISO !== todayISO) {
    return false;
  }

  const currentMinsFromMidnight = now.getHours() * 60 + now.getMinutes();
  const earliestAllowedMins = currentMinsFromMidnight + bufferMinutes;
  const slotMins = parseTime(timeStr);

  return slotMins < earliestAllowedMins;
}

/**
 * Time Utility: Get raw slot availability array for a specific date and period
 */
function getSlotsForPeriodRaw(dateISO, period) {
  const rawSlots = SLOT_CONFIG.periods[period] || [];
  return rawSlots.map((timeStr) => {
    const isPast = isPastSlot(timeStr, dateISO);
    const isApiAvailable = AvailabilityManager.isSlotAvailable(dateISO, timeStr);
    const available = !isPast && isApiAvailable;
    const endTime = calculateEndTime(timeStr);

    return {
      time: timeStr,
      endTime: endTime,
      available: available,
      displayTime: `${timeStr} – ${endTime}`
    };
  });
}

/**
 * Time Utility: Check if ALL periods in a day are empty / past
 */
function checkIfDayFullyBooked(dateISO) {
  const periods = Object.keys(SLOT_CONFIG.periods);
  for (const period of periods) {
    const slots = getSlotsForPeriodRaw(dateISO, period);
    if (slots.some(s => s.available)) {
      return false;
    }
  }
  return true;
}

/**
 * Time Utility: Generate Today and Tomorrow dates dynamically
 */
function getAvailableDates(numDays = SLOT_CONFIG.numAvailableDays) {
  const dates = [];
  const now = new Date();
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  for (let i = 0; i < numDays; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const iso = `${year}-${month}-${day}`;

    const monthStr = monthNames[d.getMonth()];
    const dayNum = d.getDate();
    const display = `${dayNum} ${monthStr}`;

    let label = "";
    if (i === 0) label = "Today";
    else if (i === 1) label = "Tomorrow";
    else label = dayNames[d.getDay()];

    const isFullyBooked = checkIfDayFullyBooked(iso);

    dates.push({
      label,
      display,
      iso,
      dayOfWeek: dayNames[d.getDay()],
      isFullyBooked
    });
  }

  return dates;
}

/**
 * Time Utility: Find the single earliest available slot overall across dates and periods
 */
function getEarliestAvailableSlotOverall() {
  const now = new Date();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  for (let i = 0; i < SLOT_CONFIG.numAvailableDays; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const iso = `${year}-${month}-${day}`;

    const label = i === 0 ? "Today" : (i === 1 ? "Tomorrow" : dayNames[d.getDay()]);
    const display = `${d.getDate()} ${monthNames[d.getMonth()]}`;

    for (const period of ["morning", "afternoon", "evening"]) {
      const rawSlots = SLOT_CONFIG.periods[period] || [];
      for (const timeStr of rawSlots) {
        const isPast = isPastSlot(timeStr, iso);
        const isApiAvailable = AvailabilityManager.isSlotAvailable(iso, timeStr);
        if (!isPast && isApiAvailable) {
          return {
            dateISO: iso,
            dateLabel: label,
            dateDisplay: display,
            period: period,
            time: timeStr
          };
        }
      }
    }
  }

  return null;
}

/**
 * Time Utility: Get slots for a specific period & date with isEarliest flag
 */
function getSlotsForPeriod(dateISO, period) {
  const rawSlots = getSlotsForPeriodRaw(dateISO, period);
  const earliestOverall = getEarliestAvailableSlotOverall();

  return rawSlots.map((s) => {
    const isEarliest = earliestOverall 
      && earliestOverall.dateISO === dateISO 
      && earliestOverall.period === period 
      && earliestOverall.time === s.time;

    return {
      ...s,
      isEarliest: isEarliest
    };
  });
}

// ==========================================================================
// 3. Translation & Question Helpers
// ==========================================================================

function getTranslation() {
  return uiTranslations[state.language] || uiTranslations.en;
}

function getQuestionsList() {
  return assessmentQuestions[state.language] || assessmentQuestions.en;
}

function getCurrentQuestion() {
  const list = getQuestionsList();
  if (state.currentStep < 0) state.currentStep = 0;
  if (state.currentStep >= list.length) state.currentStep = list.length - 1;
  return list[state.currentStep];
}

function getTotalSteps() {
  return getQuestionsList().length;
}

// ==========================================================================
// 4. Main Render Coordinator
// ==========================================================================

function renderApp() {
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  const viewport = document.getElementById("app-viewport");
  if (viewport) viewport.scrollTop = 0;

  renderHeader();
  renderProgress();

  const container = document.getElementById("app-container");
  if (!container) return;

  const currentQ = getCurrentQuestion();
  const animationClass = state.transitionDirection === "next" 
    ? "anim-slide-next" 
    : (state.transitionDirection === "prev" ? "anim-slide-prev" : "anim-fade-in");

  container.innerHTML = "";
  
  const isFinalScreen = currentQ.type === "appointment-confirmation" && state.answers["call_confirmed"];
  const card = document.createElement("div");
  card.id = "main-screen-card";
  card.className = isFinalScreen ? `screen-card-clean ${animationClass}` : `screen-card ${animationClass}`;

  card.appendChild(renderQuestionCardContent(currentQ));

  container.appendChild(card);

  saveState();
}

// ==========================================================================
// 5. Header & Progress Rendering Functions
// ==========================================================================

function renderHeader() {
  const headerEl = document.getElementById("app-header");
  if (!headerEl) return;

  const currentQ = getCurrentQuestion();
  if (!currentQ) return;

  const t = getTranslation();
  
  const TOTAL_ASSESSMENT_STEPS = 16;
  
  let percent = 0;
  if (state.currentStep === 0) {
    percent = 0;
  } else if (currentQ.type === "success" || currentQ.type === "appointment" || currentQ.type === "appointment-confirmation") {
    percent = 100;
  } else if (currentQ.id === "photo_upload" || currentQ.id === "call_number_confirmation") {
    percent = 99;
  } else {
    percent = Math.min(98, Math.round((state.currentStep / TOTAL_ASSESSMENT_STEPS) * 100));
  }

  const logoSrc = (window.shopifyBrandLogo && window.shopifyBrandLogo.trim().length > 0)
    ? window.shopifyBrandLogo
    : 'assets/logo.png';
  const logoMarkup = `<div class="brand-logo-wrapper"><img src="${logoSrc}" alt="Logo" class="brand-logo-img" onerror="this.style.display='none';" /></div>`;

  const isHalfHeader = currentQ.type === "success" || currentQ.id === "assessment_result";

  const showBackBtn = state.currentStep > 0 && !isHalfHeader;
  const backBtnMarkup = showBackBtn ? `
    <button id="btn-back" class="btn-back" aria-label="Back">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>
  ` : '';

  headerEl.style.display = "block";

  if (isHalfHeader) {
    headerEl.innerHTML = `
      <div class="brand-row">
        ${logoMarkup}
      </div>
    `;
  } else {
    headerEl.innerHTML = `
      <div class="brand-row">
        ${backBtnMarkup}
        ${logoMarkup}
      </div>
      <div class="assessment-meta-bar">
        <span class="assessment-tagline">${t.headerTagline}</span>
        <span class="step-indicator">${percent}%</span>
      </div>
      <div class="header-divider">
        <div class="header-divider-fill" style="width: ${percent}%;"></div>
        <div class="header-divider-thumb" style="left: ${percent}%;"></div>
      </div>
    `;
  }

  if (showBackBtn) {
    const backBtn = document.getElementById("btn-back");
    if (backBtn) {
      backBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        previousQuestion();
      };
    }
  }
}

function renderProgress() {
  const progressEl = document.getElementById("progress-section");
  if (!progressEl) return;
  progressEl.innerHTML = "";
  progressEl.style.display = "none";
}

function renderCustomLanguageSelector(containerId = "custom-lang-selector") {
  const wrapper = document.createElement("div");
  wrapper.id = containerId;
  wrapper.className = "custom-lang-selector-wrapper";

  const langNames = {
    en: "English",
    ml: "Malayalam",
    ta: "Tamil",
    hi: "Hindi"
  };

  const currentLabel = langNames[state.language] || "English";

  wrapper.innerHTML = `
    <button type="button" class="custom-lang-btn" id="${containerId}-btn">
      <span>${currentLabel}</span>
      <span class="custom-lang-arrow">▼</span>
    </button>
  `;

  setTimeout(() => {
    const btn = document.getElementById(`${containerId}-btn`);
    if (!btn) return;

    btn.onclick = (e) => {
      e.stopPropagation();
      const existingMenu = wrapper.querySelector(".custom-lang-menu");
      if (existingMenu) {
        existingMenu.remove();
        btn.classList.remove("open");
        return;
      }

      btn.classList.add("open");

      const menu = document.createElement("div");
      menu.className = "custom-lang-menu";

      const options = [
        { code: "ml", label: "Malayalam" },
        { code: "en", label: "English" },
        { code: "ta", label: "Tamil" },
        { code: "hi", label: "Hindi" }
      ];

      options.forEach(opt => {
        const isSelected = state.language === opt.code;
        const item = document.createElement("div");
        item.className = `custom-lang-item ${isSelected ? "selected" : ""}`;
        item.innerHTML = `
          <span>${opt.label}</span>
          ${isSelected ? `<span class="custom-lang-check">✓</span>` : ""}
        `;

        item.onclick = (event) => {
          event.stopPropagation();
          setLanguage(opt.code);
        };

        menu.appendChild(item);
      });

      wrapper.appendChild(menu);

      const closeHandler = (evt) => {
        if (!wrapper.contains(evt.target)) {
          menu.remove();
          btn.classList.remove("open");
          document.removeEventListener("click", closeHandler);
        }
      };

      setTimeout(() => {
        document.addEventListener("click", closeHandler);
      }, 0);
    };
  }, 0);

  return wrapper;
}

// ==========================================================================
// 6. Question Card Rendering & Sub-renderers
// ==========================================================================

function renderQuestionCardContent(qData) {
  const fragment = document.createDocumentFragment();
  const t = getTranslation();

  if (qData.id === "gender" || state.currentStep === 0) {
    const langHeader = document.createElement("div");
    langHeader.className = "card-lang-header";
    langHeader.innerHTML = `
      <div class="card-lang-text">
        <div class="card-lang-title">Choose Your Language</div>
        <div class="card-lang-sub">Choose Your Language To Get Started.</div>
      </div>
    `;
    langHeader.appendChild(renderCustomLanguageSelector("card-top-lang"));
    fragment.appendChild(langHeader);
  }

  if (qData.type === "appointment") {
    const greenBadge = document.createElement("span");
    greenBadge.className = "category-badge-green";
    greenBadge.textContent = t.freeCallTag;
    fragment.appendChild(greenBadge);
  } else if (qData.category && qData.type !== "appointment-confirmation" && qData.type !== "success") {
    const badge = document.createElement("span");
    badge.className = "category-badge";
    badge.textContent = qData.category;
    fragment.appendChild(badge);
  }

  if (qData.type !== "appointment-confirmation" && qData.type !== "success") {
    const title = document.createElement("h1");
    title.className = "question-title";
    title.textContent = qData.type === "appointment" ? t.expertTitle : (qData.id === "stress_level" ? "Your Daily Stress Level?" : qData.title);
    fragment.appendChild(title);
  }

  if (qData.subtitle && qData.type !== "appointment" && qData.type !== "appointment-confirmation" && qData.type !== "success" && qData.id !== "stress_level") {
    const subtitle = document.createElement("p");
    subtitle.className = "question-subtitle";
    subtitle.textContent = qData.subtitle;
    fragment.appendChild(subtitle);
  }

  const contentWrapper = document.createElement("div");
  contentWrapper.className = "question-body";

  switch (qData.type) {
    case "language":
      contentWrapper.appendChild(renderLanguageOptions(qData));
      break;
    case "radio":
      contentWrapper.appendChild(renderOptions(qData));
      break;
    case "checkbox":
      contentWrapper.appendChild(renderCheckboxOptions(qData));
      break;
    case "text":
    case "number":
    case "phone":
      contentWrapper.appendChild(renderInput(qData));
      break;
    case "image-grid":
      contentWrapper.appendChild(renderImageGrid(qData));
      break;
    case "upload":
      contentWrapper.appendChild(renderUpload(qData));
      break;
    case "call-confirmation":
      contentWrapper.appendChild(renderCallConfirmationPage(qData));
      break;
    case "stress-selector":
      contentWrapper.appendChild(renderStressSelector(qData));
      break;
    case "success":
      contentWrapper.appendChild(renderSuccess(qData));
      break;
    case "appointment":
      contentWrapper.appendChild(renderAppointment(qData));
      break;
    case "appointment-confirmation":
      contentWrapper.appendChild(renderAppointmentConfirmation(qData));
      break;
    default:
      if (qData.id === "phone") {
        contentWrapper.appendChild(renderInput(qData));
      } else {
        console.warn("Unknown question type:", qData.type);
      }
  }

  fragment.appendChild(contentWrapper);

  if (qData.type !== "appointment-confirmation") {
    const actionContainer = document.createElement("div");
    actionContainer.className = "action-container";

    const errorMsg = document.createElement("div");
    errorMsg.id = "error-message";
    errorMsg.className = "input-error-msg";
    actionContainer.appendChild(errorMsg);

    if (qData.type === "success") {
      const callNowBtn = document.createElement("button");
      callNowBtn.type = "button";
      callNowBtn.id = "btn-call-now-success";
      callNowBtn.className = "btn-call-now btn-disabled";
      callNowBtn.disabled = true;
      callNowBtn.textContent = "Watch video to continue...";
      callNowBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (callNowBtn.disabled) return;
        const phoneNumbers = (window.shopifyCallNowNumbers && Array.isArray(window.shopifyCallNowNumbers) && window.shopifyCallNowNumbers.length > 0)
          ? window.shopifyCallNowNumbers
          : ["+917593876070", "+917593876071", "+917593876073", "++917593876072"];
        const randomPhone = phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)];
        window.location.href = `tel:${randomPhone}`;
      };

      const bookBtn = document.createElement("button");
      bookBtn.type = "button";
      bookBtn.id = "btn-book-consultation-success";
      bookBtn.className = "btn-primary";
      bookBtn.disabled = false;
      bookBtn.textContent = "Book a Call Slot";
      bookBtn.onclick = () => handleNextButtonClick(qData);

      actionContainer.appendChild(callNowBtn);
      actionContainer.appendChild(bookBtn);
    } else {
      let btnLabel = t.continueBtn;
      if (qData.type === "appointment") btnLabel = t.bookNowBtn;
      if (qData.type === "call-confirmation") btnLabel = t.confirmBtn || "Confirm";

      const primaryBtn = document.createElement("button");
      primaryBtn.className = "btn-primary";
      primaryBtn.textContent = btnLabel;
      primaryBtn.onclick = () => handleNextButtonClick(qData);
      actionContainer.appendChild(primaryBtn);
    }

    fragment.appendChild(actionContainer);
  }

  if (qData.footerType) {
    fragment.appendChild(renderFooter(qData.footerType));
  }

  return fragment;
}

function renderLanguageOptions(qData) {
  const container = document.createElement("div");
  container.className = "options-container grid-2col";

  qData.options.forEach(opt => {
    const isSelected = state.language === opt.value;
    const card = document.createElement("div");
    card.className = `option-card ${isSelected ? "selected" : ""}`;
    card.onclick = () => {
      clearError();
      setLanguage(opt.value);
      setTimeout(() => {
        nextQuestion();
      }, 180);
    };

    card.innerHTML = `
      <div class="radio-icon">
        <div class="radio-inner-dot"></div>
      </div>
      <div class="option-content">
        <span class="option-label">${opt.label}</span>
      </div>
    `;
    container.appendChild(card);
  });

  return container;
}

function renderOptions(qData) {
  const container = document.createElement("div");
  const isGrid2Col = qData.layout === "grid-2col";
  container.className = `options-container ${isGrid2Col ? "grid-2col" : ""}`;

  const currentAnswer = state.answers[qData.id];

  qData.options.forEach(opt => {
    const isSelected = currentAnswer === opt.value;
    const card = document.createElement("div");
    card.className = `option-card ${isSelected ? "selected" : ""}`;

    card.onclick = () => {
      clearError();
      state.answers[qData.id] = opt.value;

      const allCards = container.querySelectorAll(".option-card");
      allCards.forEach(c => c.classList.remove("selected"));
      card.classList.add("selected");

      saveState();

      setTimeout(() => {
        nextQuestion();
      }, 180);
    };

    card.innerHTML = `
      <div class="radio-icon">
        <div class="radio-inner-dot"></div>
      </div>
      <div class="option-content">
        <span class="option-label">${opt.label}</span>
        ${opt.subtext ? `<span class="option-subtext">${opt.subtext}</span>` : ""}
      </div>
    `;
    container.appendChild(card);
  });

  return container;
}

function renderCheckboxOptions(qData) {
  const container = document.createElement("div");
  container.className = "options-container";

  let currentAnswers = state.answers[qData.id];
  if (!Array.isArray(currentAnswers)) {
    currentAnswers = [];
  }

  qData.options.forEach((opt, idx) => {
    const isSelected = currentAnswers.includes(opt.value);
    const card = document.createElement("div");
    card.className = `option-card ${isSelected ? "selected" : ""}`;

    card.onclick = () => {
      clearError();
      const currentlySelected = card.classList.contains("selected");

      if (opt.value === "none") {
        currentAnswers = currentlySelected ? [] : ["none"];
      } else {
        currentAnswers = currentAnswers.filter(val => val !== "none");
        if (currentlySelected) {
          currentAnswers = currentAnswers.filter(val => val !== opt.value);
        } else {
          currentAnswers.push(opt.value);
        }
      }
      state.answers[qData.id] = currentAnswers;

      const allCards = container.querySelectorAll(".option-card");
      qData.options.forEach((o, i) => {
        const isSel = currentAnswers.includes(o.value);
        if (allCards[i]) {
          allCards[i].classList.toggle("selected", isSel);
        }
      });

      saveState();
    };

    card.innerHTML = `
      <div class="checkbox-icon">
        <svg viewBox="0 0 24 24" fill="none">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </div>
      <div class="option-content">
        <span class="option-label">${opt.label}</span>
      </div>
    `;
    container.appendChild(card);
  });

  return container;
}

function renderInput(qData) {
  const container = document.createElement("div");
  container.className = "input-group";

  const currentValue = state.answers[qData.id] || "";

  if (qData.type === "phone" || qData.id === "phone") {
    const waDigits = String(currentValue).replace(/\D/g, "").slice(0, 10);

    const wrapper = document.createElement("div");
    wrapper.className = "phone-input-wrapper";

    wrapper.innerHTML = `
      <div class="phone-prefix-badge">
        <span class="flag-icon">🇮🇳</span>
        <span class="prefix-code">+91</span>
      </div>
      <input type="tel" id="input-${qData.id}" class="input-field phone-field-input" placeholder="${qData.placeholder || '10-digit mobile number'}" maxlength="10" value="${waDigits}">
    `;

    setTimeout(() => {
      const waInput = wrapper.querySelector(`#input-${qData.id}`);

      if (waInput) {
        waInput.oninput = (e) => {
          let val = e.target.value.replace(/\D/g, "").slice(0, 10);
          e.target.value = val;
          state.answers[qData.id] = val;
          clearError();
          saveState();
        };

        waInput.onkeydown = (e) => {
          if (e.key === "Enter") {
            handleNextButtonClick(qData);
          }
        };
      }
    }, 0);

    container.appendChild(wrapper);
    return container;
  }

  const input = document.createElement("input");
  input.id = `input-${qData.id}`;
  input.className = "input-field";
  input.type = qData.type === "number" ? "number" : "text";
  input.placeholder = qData.placeholder || "";
  input.value = currentValue;

  input.oninput = (e) => {
    state.answers[qData.id] = e.target.value;
    clearError();
    saveState();
  };

  input.onkeydown = (e) => {
    if (e.key === "Enter") {
      handleNextButtonClick(qData);
    }
  };

  container.appendChild(input);
  return container;
}

function renderImageGrid(qData) {
  const container = document.createElement("div");
  container.className = "image-grid";

  const userGender = state.answers["gender"] || "male";
  const imageList = qData.dynamicImagesByGender[userGender] || qData.dynamicImagesByGender["male"];
  const currentAnswer = state.answers[qData.id];

  imageList.forEach(item => {
    const isSelected = currentAnswer === item.value;
    const card = document.createElement("div");
    card.className = `image-card ${isSelected ? "selected" : ""}`;

    card.onclick = () => {
      clearError();
      state.answers[qData.id] = item.value;

      const allCards = container.querySelectorAll(".option-card");
      allCards.forEach(c => c.classList.remove("selected"));
      card.classList.add("selected");

      saveState();

      setTimeout(() => {
        nextQuestion();
      }, 180);
    };

    card.innerHTML = `
      <div class="image-card-img-wrapper">
        <img src="${item.image}" alt="${item.stage || item.label}" class="image-card-img" />
      </div>
      <div class="image-card-footer">
        <div class="image-stage-tag">${item.stage || item.label}</div>
      </div>
    `;
    container.appendChild(card);
  });

  return container;
}

function compressImageToTargetSize(input, callback) {
  const maxBytes = 200 * 1024;
  const img = new Image();

  function process() {
    const canvas = document.createElement("canvas");
    let width = img.width;
    let height = img.height;

    const maxDim = 1000;
    if (width > maxDim || height > maxDim) {
      if (width > height) {
        height = Math.round((height * maxDim) / width);
        width = maxDim;
      } else {
        width = Math.round((width * maxDim) / height);
        height = maxDim;
      }
    }

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    let quality = 0.82;
    let dataUrl = canvas.toDataURL("image/jpeg", quality);
    let bytes = Math.round((dataUrl.length * 3) / 4);

    while (bytes > maxBytes && quality > 0.12) {
      quality -= 0.08;
      dataUrl = canvas.toDataURL("image/jpeg", quality);
      bytes = Math.round((dataUrl.length * 3) / 4);
    }

    const kbSize = Math.round(bytes / 1024);
    callback(dataUrl, kbSize);
  }

  if (typeof input === "string") {
    img.onload = process;
    img.src = input;
  } else if (input instanceof File || input instanceof Blob) {
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = process;
      img.src = e.target.result;
    };
    reader.readAsDataURL(input);
  }
}

function renderUpload(qData) {
  const container = document.createElement("div");
  container.className = "upload-screen-wrapper";

  let photos = state.answers[qData.id];
  if (!Array.isArray(photos)) {
    photos = photos ? [photos] : [];
  }

  const placeholderImg1 = (window.shopifyUploadPlaceholderImage && window.shopifyUploadPlaceholderImage.trim().length > 0)
    ? window.shopifyUploadPlaceholderImage
    : (typeof getScalpImage === 'function' ? getScalpImage('upload_placeholder_image', 'assets/images/men/Rectangle 4.png') : 'assets/images/men/Rectangle 4.png');

  const placeholderImg2 = (window.shopifyUploadPlaceholderImage2 && window.shopifyUploadPlaceholderImage2.trim().length > 0)
    ? window.shopifyUploadPlaceholderImage2
    : (typeof getScalpImage === 'function' ? getScalpImage('upload_placeholder_image_2', 'assets/images/men/Rectangle 11.png') : placeholderImg1);

  const slot1Img = photos[0] || placeholderImg1;
  const slot2Img = photos[1] || placeholderImg2;

  container.innerHTML = `
    <div class="upload-main-card">
      <div class="upload-dual-slots-container">
        <!-- Slot 1 -->
        <div class="upload-slot-card">
          <div class="upload-image-frame ${photos[0] ? 'has-uploaded' : ''}" id="upload-frame-0">
            <img src="${slot1Img}" alt="Scalp Photo 1" class="upload-display-img" />
            ${photos[0] ? `<button type="button" class="btn-slot-remove" data-slot="0" title="Remove photo">✕</button>` : ''}
          </div>
          <button type="button" class="btn-take-photo-slot" id="btn-take-photo-0">
            <span>Take Photo</span>
          </button>
        </div>

        <!-- Slot 2 -->
        <div class="upload-slot-card">
          <div class="upload-image-frame ${photos[1] ? 'has-uploaded' : ''}" id="upload-frame-1">
            <img src="${slot2Img}" alt="Scalp Photo 2" class="upload-display-img" />
            ${photos[1] ? `<button type="button" class="btn-slot-remove" data-slot="1" title="Remove photo">✕</button>` : ''}
          </div>
          <button type="button" class="btn-take-photo-slot" id="btn-take-photo-1">
            <span>Take Photo</span>
          </button>
        </div>
      </div>

      <div class="upload-action-buttons-row">
        <button type="button" class="btn-choose-gallery-pill" id="btn-choose-gallery-file">
          <span>Choose From Gallery</span>
        </button>
      </div>

      <input type="file" id="file-upload-input-multi" class="file-input-hidden" style="display:none !important;" accept="image/*" multiple />
      <input type="file" id="camera-capture-input-0" class="file-input-hidden" style="display:none !important;" accept="image/*" capture="user" />
      <input type="file" id="camera-capture-input-1" class="file-input-hidden" style="display:none !important;" accept="image/*" capture="user" />
    </div>
  `;

  setTimeout(() => {
    const multiFileInput = container.querySelector("#file-upload-input-multi");
    const galleryBtn = container.querySelector("#btn-choose-gallery-file");
    const removeBtns = container.querySelectorAll(".btn-slot-remove");

    [0, 1].forEach(slotIdx => {
      const camBtn = container.querySelector(`#btn-take-photo-${slotIdx}`);
      const camInput = container.querySelector(`#camera-capture-input-${slotIdx}`);

      if (camBtn && camInput) {
        camBtn.onclick = (e) => {
          e.preventDefault();
          clearError();
          camInput.click();
        };
      }

      if (camInput) {
        camInput.onchange = (e) => {
          if (e.target.files && e.target.files[0]) {
            processFileForSlot(e.target.files[0], slotIdx);
          }
        };
      }
    });

    function processFileForSlot(file, slotIdx) {
      if (!file || !file.type || !file.type.startsWith("image/")) {
        showError("Please select a valid image file (JPG, PNG).");
        return;
      }
      compressImageToTargetSize(file, (compressedDataUrl, kbSize) => {
        clearError();
        let currentPhotos = state.answers[qData.id];
        if (!Array.isArray(currentPhotos)) currentPhotos = currentPhotos ? [currentPhotos] : [];
        currentPhotos[slotIdx] = compressedDataUrl;
        state.answers[qData.id] = currentPhotos;
        state.answers[qData.id + `_${slotIdx}_kb`] = kbSize;

        if (typeof OnboardingAPI !== "undefined") {
          OnboardingAPI.uploadPhoto(null, file).then(res => {
            if (res && res.data && res.data.user && res.data.user.diagnosis_assets && res.data.user.diagnosis_assets.scalp_photo_url) {
              state.answers["scalp_photo_url_" + slotIdx] = res.data.user.diagnosis_assets.scalp_photo_url;
            }
          }).catch(err => console.warn("API photo upload warning:", err));
        }

        renderApp();
      });
    }

    if (galleryBtn && multiFileInput) {
      galleryBtn.onclick = (e) => {
        e.preventDefault();
        clearError();
        multiFileInput.click();
      };

      multiFileInput.onchange = (e) => {
        const files = Array.from(e.target.files || []).filter(f => f.type && f.type.startsWith("image/"));
        if (files.length === 0) return;

        const selectedFiles = files.slice(0, 2);

        let currentPhotos = state.answers[qData.id];
        if (!Array.isArray(currentPhotos)) currentPhotos = currentPhotos ? [currentPhotos] : [];

        let processCount = 0;

        selectedFiles.forEach((file, index) => {
          let targetSlot = 0;

          if (selectedFiles.length === 1) {
            if (!currentPhotos[0]) targetSlot = 0;
            else if (!currentPhotos[1]) targetSlot = 1;
            else targetSlot = 0;
          } else {
            targetSlot = index;
          }

          compressImageToTargetSize(file, (compressedDataUrl, kbSize) => {
            currentPhotos[targetSlot] = compressedDataUrl;
            state.answers[qData.id + `_${targetSlot}_kb`] = kbSize;
            processCount++;

            if (processCount === selectedFiles.length) {
              clearError();
              state.answers[qData.id] = currentPhotos;
              renderApp();
            }
          });
        });
      };
    }

    removeBtns.forEach(btn => {
      btn.onclick = (e) => {
        e.stopPropagation();
        const slotIdx = parseInt(btn.dataset.slot, 10);
        let currentPhotos = state.answers[qData.id];
        if (Array.isArray(currentPhotos)) {
          currentPhotos[slotIdx] = null;
          if (!currentPhotos[0] && !currentPhotos[1]) {
            currentPhotos = [];
          }
          state.answers[qData.id] = currentPhotos;
        } else {
          delete state.answers[qData.id];
        }
        renderApp();
      };
    });
  }, 0);

  return container;
}

function renderCallConfirmationPage(qData) {
  const container = document.createElement("div");
  container.className = "call-confirmation-screen-wrapper";

  const primaryPhone = state.answers["calling_phone"] || state.answers["phone"] || "";
  const rawPrimaryDigits = String(primaryPhone).replace(/\D/g, "").slice(-10);
  const existingAlt = state.answers["alt_phone"] || "";

  container.innerHTML = `
    <!-- Primary Calling Number Dialing Field with India Flag Prefix Badge -->
    <div class="phone-field-group">
      <div class="phone-input-wrapper">
        <div class="phone-prefix-badge">
          <span class="flag-icon">🇮🇳</span>
          <span class="prefix-code">+91</span>
        </div>
        <input type="tel" id="input-primary-phone-confirm" class="input-field phone-field-input" placeholder="Primary calling number" maxlength="10" value="${rawPrimaryDigits}">
      </div>
    </div>

    <!-- Don't Miss Your Call Box (Blue Bordered Card) -->
    <div class="dont-miss-call-card">
      <div class="dont-miss-call-title">Don't Miss Your Call</div>
      <div class="dont-miss-call-sub">Please Provide Your Alternate Number Where We Can Call You If The Primary Number Is Unreachable.</div>

      <div class="alt-phone-input-field-wrapper">
        <div class="phone-prefix-badge" style="position: static; padding: 0;">
          <span class="flag-icon">🇮🇳</span>
          <span class="prefix-code">+91</span>
        </div>
        <input type="tel" id="input-alt-phone-confirm" class="alt-phone-input-element" placeholder="Add Alternate Number" maxlength="10" value="${existingAlt}" />
      </div>
    </div>
  `;

  setTimeout(() => {
    const primaryInput = container.querySelector("#input-primary-phone-confirm");
    const altInput = container.querySelector("#input-alt-phone-confirm");

    if (primaryInput) {
      primaryInput.oninput = (e) => {
        let val = e.target.value.replace(/\D/g, "").slice(0, 10);
        e.target.value = val;
        state.answers["calling_phone"] = val;
        if (!state.answers["phone"]) {
          state.answers["phone"] = val;
        }
        clearError();
        saveState();
      };

      primaryInput.onkeydown = (e) => {
        if (e.key === "Enter") {
          handleNextButtonClick(qData);
        }
      };
    }

    if (altInput) {
      altInput.oninput = (e) => {
        let val = e.target.value.replace(/\D/g, "").slice(0, 10);
        e.target.value = val;
        state.answers["alt_phone"] = val;
        clearError();
        saveState();
      };

      altInput.onkeydown = (e) => {
        if (e.key === "Enter") {
          handleNextButtonClick(qData);
        }
      };
    }
  }, 0);

  return container;
}

function renderStressSelector(qData) {
  const container = document.createElement("div");
  container.className = "stress-circles-wrapper";

  const currentVal = state.answers[qData.id] ?? null;

  const circlesRow = document.createElement("div");
  circlesRow.className = "stress-circles-row";

  for (let i = 1; i <= 5; i++) {
    const circleBtn = document.createElement("button");
    circleBtn.type = "button";
    circleBtn.className = `stress-circle-btn ${Number(currentVal) === i ? "selected" : ""}`;
    circleBtn.textContent = i;

    circleBtn.onclick = () => {
      clearError();
      state.answers[qData.id] = i;

      const allCircles = circlesRow.querySelectorAll(".stress-circle-btn");
      allCircles.forEach((c, idx) => {
        c.classList.toggle("selected", idx + 1 === i);
      });

      saveState();

      setTimeout(() => {
        nextQuestion();
      }, 180);
    };

    circlesRow.appendChild(circleBtn);
  }

  const labelsRow = document.createElement("div");
  labelsRow.className = "stress-labels-row";
  labelsRow.innerHTML = `
    <span>Calm</span>
    <span>Very Stressed</span>
  `;

  const gradientBar = document.createElement("div");
  gradientBar.className = "stress-gradient-bar";

  container.appendChild(circlesRow);
  container.appendChild(labelsRow);
  container.appendChild(gradientBar);

  return container;
}

function renderSuccess(qData) {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {}

  sendTeleCRMLeadUpdate();

  const container = document.createElement("div");

  const videoSrc = (window.shopifyCompletionVideo && window.shopifyCompletionVideo.trim().length > 0)
    ? window.shopifyCompletionVideo
    : "https://assets.mixkit.co/videos/preview/mixkit-man-looking-at-the-camera-41584-large.mp4";

  container.innerHTML = `
    <svg class="hvb__check animate" viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <circle cx="32" cy="32" r="29" stroke="currentColor" stroke-width="4"/>
      <path d="M20 33.5 28.5 42 45 24.5" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>

    <h1 class="call-booked-heading">Assessment<br>Completed!</h1>
    <p class="call-booked-subheading">Our Hair Expert Will Call You On Your Registered Number.</p>

    <div class="completed-video-card">
      <video class="completed-video-element" id="success-video-element" playsinline preload="metadata">
        <source src="${videoSrc}" type="video/mp4">
      </video>
      
      <div class="video-rewind-overlay" id="video-rewind-btn" style="display: none;">
        <svg height="64px" width="64px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" viewBox="-3.3 -3.3 36.56 36.56" xml:space="preserve" fill="#ffffff" stroke="#ffffff">
          <g><path style="fill:#ffffff;" d="M29.133,9.215c-0.775-0.428-1.751-0.146-2.178,0.63l-0.518,0.937 c-1.772-5.424-6.874-9.356-12.882-9.356C6.081,1.426,0,7.507,0,14.981s6.081,13.555,13.555,13.555 c0.886,0,1.604-0.718,1.604-1.603c0-0.885-0.718-1.603-1.604-1.603c-5.706,0-10.349-4.643-10.349-10.35S7.849,4.632,13.555,4.632 c4.525,0,8.372,2.924,9.775,6.978l-0.876-0.483c-0.775-0.428-1.751-0.147-2.178,0.629c-0.428,0.776-0.147,1.751,0.629,2.178 l3.169,1.748c0.261,0.531,0.801,0.901,1.434,0.901c0.16,0,0.311-0.031,0.456-0.074c0.009,0,0.016,0.003,0.025,0.003 c0.565,0,1.112-0.298,1.406-0.829l2.368-4.292C30.191,10.618,29.909,9.643,29.133,9.215z"></path></g>
        </svg>
      </div>

      <div class="play-icon-overlay" id="success-video-play-btn" style="display: none;">▶</div>
    </div>
  `;

  setTimeout(() => {
    const playBtn = container.querySelector("#success-video-play-btn");
    const rewindBtn = container.querySelector("#video-rewind-btn");
    const videoEl = container.querySelector("#success-video-element");

    if (videoEl) {
      const callNowBtn = document.getElementById("btn-call-now-success");
      const bookBtn = document.getElementById("btn-book-consultation-success");

      const updateVideoLockState = () => {
        if (!videoEl || !callNowBtn) return;
        const totalDuration = (videoEl.duration && !isNaN(videoEl.duration) && videoEl.duration > 0) ? videoEl.duration : 40;
        const currentPos = videoEl.currentTime || 0;
        const remainingSecs = Math.max(0, Math.ceil(totalDuration - currentPos));

        if (videoEl.ended || remainingSecs <= 0) {
          callNowBtn.disabled = false;
          callNowBtn.classList.remove("btn-disabled");
          callNowBtn.textContent = "Call Now";
        } else {
          callNowBtn.disabled = true;
          callNowBtn.classList.add("btn-disabled");
          callNowBtn.textContent = `Watch video to continue (${remainingSecs}s)...`;
        }

        if (bookBtn) {
          bookBtn.disabled = false;
          bookBtn.classList.remove("btn-disabled");
          bookBtn.textContent = "Book a Call Slot";
        }
      };

      updateVideoLockState();

      videoEl.ontimeupdate = updateVideoLockState;
      videoEl.onloadedmetadata = updateVideoLockState;

      videoEl.muted = false;

      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          if (playBtn) playBtn.style.display = "none";
        }).catch(() => {
          if (playBtn) playBtn.style.display = "flex";
        });
      }

      const replayVideoWithSound = () => {
        videoEl.currentTime = 0;
        videoEl.muted = false;
        videoEl.play();
        if (rewindBtn) rewindBtn.style.display = "none";
        if (playBtn) playBtn.style.display = "none";
        updateVideoLockState();
      };

      videoEl.onended = () => {
        updateVideoLockState();
        if (rewindBtn) rewindBtn.style.display = "flex";
        if (playBtn) playBtn.style.display = "none";
      };

      if (rewindBtn) rewindBtn.onclick = replayVideoWithSound;

      if (playBtn) {
        playBtn.onclick = () => {
          if (videoEl.ended) {
            replayVideoWithSound();
          } else {
            videoEl.muted = false;
            videoEl.play();
            playBtn.style.display = "none";
          }
        };
      }

      videoEl.onclick = () => {
        if (videoEl.ended) {
          replayVideoWithSound();
        } else if (videoEl.paused) {
          videoEl.play();
          if (playBtn) playBtn.style.display = "none";
        } else {
          videoEl.pause();
          if (playBtn) playBtn.style.display = "flex";
        }
      };
    }
  }, 0);

  return container;
}

function renderAppointment(qData) {
  const container = document.createElement("div");
  const t = getTranslation();

  const availableDates = getAvailableDates(SLOT_CONFIG.numAvailableDays);
  const earliestOverall = getEarliestAvailableSlotOverall();

  if (!state.booking) {
    let initialDate = availableDates.find(d => !d.isFullyBooked) || availableDates[0];
    let initialPeriod = "morning";
    let initialSlot = null;
    let initialEndTime = null;

    if (earliestOverall) {
      const matchDate = availableDates.find(d => d.iso === earliestOverall.dateISO);
      if (matchDate) {
        initialDate = matchDate;
        initialPeriod = earliestOverall.period;
        initialSlot = earliestOverall.time;
        initialEndTime = calculateEndTime(earliestOverall.time);
      }
    }

    state.booking = {
      dateISO: initialDate.iso,
      dateLabel: initialDate.label,
      dateDisplay: initialDate.display,
      period: initialPeriod,
      slot: initialSlot,
      slotEndTime: initialEndTime
    };
  }

  const currentISO = state.booking.dateISO;
  let activePeriod = state.booking.period || "morning";

  const datePillsHtml = availableDates.map(d => {
    const isActive = state.booking.dateISO === d.iso;
    const isBooked = d.isFullyBooked;

    return `
      <div class="date-pill ${isActive ? 'active' : 'inactive'} ${isBooked ? 'disabled' : ''}" 
           data-iso="${d.iso}" data-label="${d.label}" data-display="${d.display}"
           style="${isBooked ? 'opacity:0.5; cursor:not-allowed;' : ''}">
        <div class="date-pill-radio">
          <div class="date-pill-radio-dot"></div>
        </div>
        <div class="date-pill-text">
          <span class="date-pill-title">${d.label}</span>
          <span class="date-pill-sub">${isBooked ? 'FULLY BOOKED' : d.display}</span>
        </div>
      </div>
    `;
  }).join("");

  const apptHeaderRow = document.createElement("div");
  apptHeaderRow.className = "appointment-header-row";
  apptHeaderRow.innerHTML = `
    <div class="appointment-header-left">
      <span class="appointment-header-title">${t.chooseLanguageTitle}</span>
      <span class="appointment-header-sub">${t.chooseLanguageSub}</span>
    </div>
  `;
  apptHeaderRow.appendChild(renderCustomLanguageSelector("appt-top-lang"));
  container.appendChild(apptHeaderRow);

  const restWrapper = document.createElement("div");
  restWrapper.innerHTML = `
    <div class="section-label-bold">${t.chooseDateTitle}</div>
    <div class="date-pills-row">
      ${datePillsHtml}
    </div>

    <div class="section-label-bold">${t.chooseTimeTitle}</div>
    <div class="time-tabs-row">
      <div class="time-tab ${activePeriod === 'morning' ? 'active' : 'inactive'}" data-period="morning">${t.morning}</div>
      <div class="time-tab ${activePeriod === 'afternoon' ? 'active' : 'inactive'}" data-period="afternoon">${t.afternoon}</div>
      <div class="time-tab ${activePeriod === 'evening' ? 'active' : 'inactive'}" data-period="evening">${t.evening}</div>
    </div>

    <div class="time-slots-wrapper" id="time-slots-wrapper">
      ${renderSlotsHtml(currentISO, activePeriod)}
    </div>
  `;
  container.appendChild(restWrapper);

  function renderSlotsHtml(dateISO, period) {
    const slots = getSlotsForPeriod(dateISO, period);

    if (slots.length === 0 || slots.every(s => !s.available)) {
      return `
        <div class="no-slots-box">
          ${t.noSlotsMsg.replace('\n', '<br/>')}
        </div>
      `;
    }

    const slotCardsHtml = slots.map((s) => {
      const isSelected = state.booking && state.booking.slot === s.time && state.booking.dateISO === dateISO;
      const isEarliest = s.isEarliest;

      if (!s.available) {
        return `
          <div class="time-slot-card-container">
            <div class="time-slot-card disabled" style="opacity: 0.45; cursor: not-allowed; background-color: #F3F4F6; border-color: #E5E7EB;">
              <span class="time-slot-label" style="text-decoration: line-through; color: #9CA3AF;">${s.time}</span>
            </div>
          </div>
        `;
      }

      return `
        <div class="time-slot-card-container">
          ${isEarliest ? `<span class="earliest-badge">${t.earliestTag}</span>` : ''}
          <div class="time-slot-card ${isSelected ? 'selected' : ''}" data-slot="${s.time}" data-endtime="${s.endTime}">
            <div class="radio-icon"><div class="radio-inner-dot"></div></div>
            <span class="time-slot-label">${s.time}</span>
          </div>
        </div>
      `;
    }).join("");

    return `<div class="time-slots-grid">${slotCardsHtml}</div>`;
  }

  setTimeout(() => {
    const datePills = container.querySelectorAll(".date-pill:not(.disabled)");
    datePills.forEach(pill => {
      pill.onclick = () => {
        clearError();
        const iso = pill.dataset.iso;
        const label = pill.dataset.label;
        const display = pill.dataset.display;

        state.booking.dateISO = iso;
        state.booking.dateLabel = label;
        state.booking.dateDisplay = display;
        state.booking.slot = null;
        state.booking.slotEndTime = null;

        const availableSlots = getSlotsForPeriod(iso, state.booking.period).filter(s => s.available);
        if (availableSlots.length > 0) {
          state.booking.slot = availableSlots[0].time;
          state.booking.slotEndTime = availableSlots[0].endTime;
        }

        datePills.forEach(p => {
          const isAct = p.dataset.iso === iso;
          p.classList.toggle("active", isAct);
          p.classList.toggle("inactive", !isAct);
        });

        const slotsWrapper = document.getElementById("time-slots-wrapper");
        if (slotsWrapper) {
          slotsWrapper.innerHTML = renderSlotsHtml(iso, state.booking.period);
          attachSlotEvents();
        }

        saveState();
      };
    });

    const periodTabs = container.querySelectorAll(".time-tab");
    const slotsWrapper = document.getElementById("time-slots-wrapper");

    periodTabs.forEach(tab => {
      tab.onclick = () => {
        clearError();
        const period = tab.dataset.period;
        state.booking.period = period;

        periodTabs.forEach(t => {
          const isAct = t.dataset.period === period;
          t.classList.toggle("active", isAct);
          t.classList.toggle("inactive", !isAct);
        });

        const availableSlots = getSlotsForPeriod(state.booking.dateISO, period).filter(s => s.available);
        if (availableSlots.length > 0) {
          state.booking.slot = availableSlots[0].time;
          state.booking.slotEndTime = availableSlots[0].endTime;
        } else {
          state.booking.slot = null;
          state.booking.slotEndTime = null;
        }

        if (slotsWrapper) {
          slotsWrapper.innerHTML = renderSlotsHtml(state.booking.dateISO, period);
          attachSlotEvents();
        }

        saveState();
      };
    });

    function attachSlotEvents() {
      const slotCards = container.querySelectorAll(".time-slot-card:not(.disabled)");
      slotCards.forEach(card => {
        card.onclick = () => {
          clearError();
          const slotTime = card.dataset.slot;
          const endTime = card.dataset.endtime;

          state.booking.slot = slotTime;
          state.booking.slotEndTime = endTime;

          slotCards.forEach(c => c.classList.remove("selected"));
          card.classList.add("selected");

          saveState();
        };
      });
    }

    attachSlotEvents();
  }, 0);

  return container;
}

function renderAppointmentConfirmation(qData) {
  const container = document.createElement("div");
  const t = getTranslation();

  const isConfirmed = state.answers["call_confirmed"];
  const userPhone = state.answers["phone"] || "952 660 4432";
  
  const b = state.booking || {
    dateLabel: "Today",
    dateDisplay: "14 July",
    slot: "10:00 AM",
    slotEndTime: "10:30 AM"
  };

  const formattedTimeRange = `${b.slot} – ${b.slotEndTime || calculateEndTime(b.slot)}`;

  if (!isConfirmed) {
    container.innerHTML = `
      <div style="font-size:24px; font-weight:800; color:#111827; margin-bottom:4px;">Call Booked!</div>
      <div style="font-size:13px; color:#9CA3AF; margin-bottom:20px;">We Will Call You On</div>

      <div class="primary-phone-pill">
        <div class="primary-phone-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
          </svg>
        </div>
        <div class="primary-phone-info">
          <span class="primary-phone-label">Primary Number</span>
          <span class="primary-phone-number">+91 ${userPhone}</span>
        </div>
      </div>

      <div style="font-size:12px; color:#9CA3AF; margin-bottom:10px;">Your Call Is Scheduled On :</div>
      <div class="scheduled-time-badge">${b.dateLabel} Time : ${formattedTimeRange}</div>

      <div class="alt-phone-box">
        <div class="alt-phone-title">Don't Miss Your Call</div>
        <div class="alt-phone-sub">Please Provide Your Alternate Number Where We Can Call You If The Primary Number Is Unreachable.</div>
        <div class="alt-phone-input-row">
          <div class="alt-phone-input-wrapper" id="alt-phone-wrapper">
            <span class="alt-phone-prefix">+91</span>
            <input type="tel" class="alt-phone-input" id="alt-phone-input" placeholder="Add Alternate Number" maxLength="10" value="${state.answers['alt_phone'] || ''}" />
          </div>
          <button class="btn-save-alt" id="btn-save-alt">Save</button>
        </div>
      </div>

      <div class="action-container" style="margin-top:20px;">
        <div id="error-message" class="input-error-msg"></div>
        <button class="btn-primary" id="btn-confirm-call">Confirm</button>
        <button type="button" class="btn-call-now" id="btn-confirm-call-now">Call Now</button>
      </div>
    `;

    setTimeout(() => {
      const confirmBtn = document.getElementById("btn-confirm-call");
      const saveAltBtn = document.getElementById("btn-save-alt");
      const altInput = document.getElementById("alt-phone-input");
      const callNowBtn1 = document.getElementById("btn-confirm-call-now");

      if (callNowBtn1) {
        callNowBtn1.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const phoneNumbers = (window.shopifyCallNowNumbers && Array.isArray(window.shopifyCallNowNumbers) && window.shopifyCallNowNumbers.length > 0)
            ? window.shopifyCallNowNumbers
            : ["+917593876070", "+917593876071", "+917593876073", "++917593876072"];
          const randomPhone = phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)];
          window.location.href = `tel:${randomPhone}`;
        };
      }

      if (saveAltBtn && altInput) {
        altInput.oninput = (e) => {
          e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
          clearError();
        };

        saveAltBtn.onclick = () => {
          const val = altInput.value.trim();
          if (val && val.length > 0 && val.length < 10) {
            showError("Please enter a valid 10-digit alternate phone number.");
            return;
          }
          if (val && val === userPhone.replace(/\D/g, "")) {
            showError("Alternate number cannot be the same as the primary number.");
            return;
          }
          if (val) {
            state.answers["alt_phone"] = val;
            saveAltBtn.textContent = "✓ Saved";
            setTimeout(() => { saveAltBtn.textContent = "Save"; }, 2000);
          }
        };
      }

      if (confirmBtn) {
        confirmBtn.onclick = async () => {
          const altVal = altInput ? altInput.value.replace(/\D/g, "") : "";
          if (altVal.length > 0) {
            if (altVal === userPhone.replace(/\D/g, "")) {
              showError("Alternate number cannot be the same as the primary number.");
              return;
            }
            if (altVal.length !== 10) {
              showError("Enter a valid 10-digit number.");
              return;
            }
            state.answers["alt_phone"] = altVal;
          }

          confirmBtn.disabled = true;
          confirmBtn.textContent = "Confirming...";

          await sendTeleCRMLeadUpdate();

          state.answers["call_confirmed"] = true;
          state.transitionDirection = "next";
          renderApp();
        };
      }
    }, 0);

  } else {
    container.innerHTML = `
      <div class="checkmark-circle-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </div>

      <h1 class="call-booked-heading">Call Booked!</h1>
      <p class="call-booked-subheading">Our Hair Expert Will Call You On Your Registered Number.</p>

      <div class="call-booked-meta-row">
        <div class="meta-item">
          <span>🕒</span>
          <span>${formattedTimeRange}</span>
        </div>
        <div class="meta-item">
          <span>📅</span>
          <span>${b.dateLabel === 'Today' || b.dateLabel === 'Tomorrow' ? b.dateDisplay : b.dateLabel}</span>
        </div>
      </div>

      <div class="action-container" style="margin-top:auto;">
        <button class="btn-call-now" id="btn-call-now-confirmed">Call Now</button>
        <button class="btn-secondary-outline" id="btn-reschedule">Reschedule</button>
      </div>
    `;

    setTimeout(() => {
      const rescheduleBtn = document.getElementById("btn-reschedule");
      const callNowBtn = document.getElementById("btn-call-now-confirmed");

      if (rescheduleBtn) {
        rescheduleBtn.onclick = () => {
          delete state.answers["call_confirmed"];
          if (state.booking) {
            state.booking.slot = null;
            state.booking.slotEndTime = null;
          }
          const apptStep = getQuestionsList().findIndex(q => q.type === "appointment");
          goToQuestion(apptStep >= 0 ? apptStep : 17);
        };
      }

      if (callNowBtn) {
        callNowBtn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const phoneNumbers = (window.shopifyCallNowNumbers && Array.isArray(window.shopifyCallNowNumbers) && window.shopifyCallNowNumbers.length > 0)
            ? window.shopifyCallNowNumbers
            : ["+917593876070", "+917593876071", "+917593876073", "++917593876072"];
          const randomPhone = phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)];
          window.location.href = `tel:${randomPhone}`;
        };
      }
    }, 0);
  }

  return container;
}

function renderFooter(footerType) {
  const footer = document.createElement("p");
  footer.className = "footer-disclaimer";

  const t = getTranslation();
  if (footerType === "privacy") {
    footer.textContent = t.privacyFooter;
  } else if (footerType === "phone") {
    footer.textContent = t.phoneFooter;
  }

  return footer;
}

// ==========================================================================
// 7. Navigation & Strict Validation Logic with Facebook Pixel Event Integration
// ==========================================================================

async function handleNextButtonClick(qData) {

  // console.log("STEP ID:", qData.id);

  if (validateCurrentStep(qData)) {
    const val = state.answers[qData.id];

    const userPhone = state.answers["phone"] || "";
const userName  = state.answers["name"]  || "";

// Normalise phone to E.164 digits: 919876543210
const normalisePhone = (raw) => {
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return "91" + digits;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  if (digits.length === 11 && digits.startsWith("0")) return "91" + digits.slice(1);
  return digits;
};

const phoneE164 = normalisePhone(userPhone);

if (typeof fbq === 'function') {

  // Re-init with Advanced Matching as soon as we have the phone.
  // fbq hashes these in the browser. Must run BEFORE any track call.
  if (phoneE164) {
    const matchData = { ph: phoneE164, external_id: phoneE164 };
    const first = String(userName).trim().toLowerCase().split(" ")[0];
    if (first) matchData.fn = first;
    fbq('init', '663734053227469', matchData);
  }

  fbq('trackCustom', 'FormFieldCompleted', {
    step_id: qData.id,
    step_number: state.currentStep + 1,
    content_name: `step_${qData.id}_completed`
  });

  if (qData.id === "phone" || qData.type === "phone") {
    fbq('track', 'Lead', { content_name: 'quiz_step_1_completed' });
  }

  if (qData.id === "scalp_type") {
    fbq('trackCustom', 'QuizStep2Complete', { content_name: 'quiz_step_2_hair_health' });
  }

  if (qData.id === "previous_treatments") {
    fbq('trackCustom', 'QuizStep3Complete', { content_name: 'quiz_step_3_lifestyle' });
  }

  if (qData.id === "photo_upload") {
    fbq('track', 'CompleteRegistration', { content_name: 'quiz_step_4_assessment' });
  }
}

    if (typeof OnboardingAPI !== "undefined") {
      if (qData.id === "phone" || qData.type === "phone") {
        sendTeleCRMCreateLead();
        OnboardingAPI.startUser({
          name: state.answers["name"] || "Customer",
          phone: String(val || "").replace(/\D/g, "").slice(-10),
          calling_phone: String(state.answers["calling_phone"] || val || "").replace(/\D/g, "").slice(-10),
          country_code: "+91",
          preferred_language: state.language || "en"
        }).then(() => {
          const payload = OnboardingAPI.buildPayloadFromState(state.answers, state.booking || {});
          OnboardingAPI.saveOnboarding(null, payload);
        }).catch(err => console.warn("API startUser warning:", err));
      } else {
        OnboardingAPI.saveAnswer(null, qData.id, val).catch(() => {});
        const payload = OnboardingAPI.buildPayloadFromState(state.answers, state.booking || {});
        OnboardingAPI.saveOnboarding(null, payload).catch(() => {});
      }
    }

    if (qData.type === "call-confirmation") {
      await sendTeleCRMLeadUpdate();
      nextQuestion();
    } else if (qData.type === "success") {
      if (typeof OnboardingAPI !== "undefined") {
        OnboardingAPI.completeOnboarding().catch(() => {});
      }

      await sendTeleCRMLeadUpdate();

      const userPhone = state.answers["phone"] || "";
      const userName = state.answers["name"] || "";
      const userLang = state.language || "en";

      const scheduleUrl = (window.shopifySchedulePageUrl && window.shopifySchedulePageUrl.trim().length > 0)
        ? window.shopifySchedulePageUrl
        : "/pages/book-your-consultation";

      const targetUrl = `${scheduleUrl}?phone=${encodeURIComponent(userPhone)}&name=${encodeURIComponent(userName)}&lang=${encodeURIComponent(userLang)}`;
      window.location.href = targetUrl;
    } else if (qData.type === "appointment-confirmation") {
      if (typeof OnboardingAPI !== "undefined") {
        OnboardingAPI.clearStoredUserId();
      }
      state.currentStep = 0;
      state.answers = {};
      state.booking = null;
      state.transitionDirection = "fade";
      renderApp();
    } else {
      nextQuestion();
    }
  }
}

function validateCurrentStep(qData) {
  const t = getTranslation();
  const val = state.answers[qData.id];

  if (qData.type === "language") {
    return true;
  }

  if (qData.type === "stress-selector" || qData.id === "stress_level") {
    if (val === undefined || val === null || val === "") {
      showError("Please select your stress level to continue.");
      return false;
    }
  }

  if (qData.type === "radio" || qData.type === "image-grid") {
    if (!val) {
      showError(t.selectOptionError);
      return false;
    }
  }

  if (qData.type === "checkbox") {
    if (!Array.isArray(val) || val.length === 0) {
      showError(t.selectOptionError);
      return false;
    }
  }

  if (qData.type === "text") {
    const nameStr = String(val || "").trim();
    if (!val || nameStr.length < 2) {
      showError(t.enterNameError);
      highlightInput(`input-${qData.id}`);
      return false;
    }
  }

  if (qData.type === "phone" || qData.id === "phone") {
    const waCleaned = String(state.answers[qData.id] || "").replace(/\D/g, "");
    if (waCleaned.length < 10) {
      showError(t.enterPhoneError || "Please enter a valid 10-digit mobile number.");
      highlightInput(`input-${qData.id}`);
      return false;
    }
  }

  if (qData.type === "call-confirmation") {
    const primaryVal = String(state.answers["calling_phone"] || state.answers["phone"] || "").replace(/\D/g, "");
    if (primaryVal.length < 10) {
      showError("Please enter a valid 10-digit primary calling number.");
      highlightInput("input-primary-phone-confirm");
      return false;
    }

    const altVal = String(state.answers["alt_phone"] || "").replace(/\D/g, "");
    if (altVal.length > 0) {
      if (altVal.length !== 10) {
        showError("Please enter a valid 10-digit alternate phone number.");
        highlightInput("input-alt-phone-confirm");
        return false;
      }
      if (altVal === primaryVal) {
        showError("Alternate number cannot be the same as the primary calling number.");
        highlightInput("input-alt-phone-confirm");
        return false;
      }
    }
  }

  if (qData.type === "number") {
    const num = Number(val);
    if (!val || isNaN(num) || num < 10 || num > 100) {
      showError(t.enterAgeError);
      highlightInput(`input-${qData.id}`);
      return false;
    }
  }

  if (qData.type === "upload") {
    const photos = Array.isArray(val) ? val.filter(Boolean) : (val ? [val] : []);
    if (photos.length === 0) {
      showError("Please take or upload at least one scalp photo to continue.");
      return false;
    }
  }

  if (qData.type === "appointment") {
    if (!state.booking || !state.booking.dateISO) {
      showError("Please select an appointment date.");
      return false;
    }
    if (!state.booking.slot) {
      showError(t.selectTimeError || "Please select an available time slot to continue.");
      return false;
    }
  }

  clearError();
  return true;
}

function highlightInput(inputId) {
  const el = document.getElementById(inputId);
  if (el) {
    el.classList.add("input-error");
    el.focus();
  }
}

function showError(message) {
  const errEl = document.getElementById("error-message");
  const cardEl = document.getElementById("main-screen-card");

  if (errEl) {
    errEl.innerHTML = `⚠️ <span>${message}</span>`;
    errEl.classList.add("visible");
  }

  if (cardEl) {
    cardEl.classList.remove("shake-error");
    void cardEl.offsetWidth;
    cardEl.classList.add("shake-error");
  }
}

function clearError() {
  const errEl = document.getElementById("error-message");
  if (errEl) {
    errEl.textContent = "";
    errEl.classList.remove("visible");
  }

  const inputs = document.querySelectorAll(".input-field");
  inputs.forEach(input => input.classList.remove("input-error"));

  const cardEl = document.getElementById("main-screen-card");
  if (cardEl) {
    cardEl.classList.remove("shake-error");
  }
}

function nextQuestion() {
  if (state.currentStep < getTotalSteps() - 1) {
    state.currentStep++;
    state.transitionDirection = "next";
    renderApp();
  }
}

function previousQuestion() {
  if (state.currentStep > 0) {
    state.currentStep--;
    state.transitionDirection = "prev";
    renderApp();
  }
}

function goToQuestion(stepIndex) {
  if (stepIndex >= 0 && stepIndex < getTotalSteps()) {
    state.transitionDirection = stepIndex > state.currentStep ? "next" : "prev";
    state.currentStep = stepIndex;
    renderApp();
  }
}

function setLanguage(langCode) {
  if (uiTranslations[langCode]) {
    state.language = langCode;
    state.answers["language"] = langCode;
    state.transitionDirection = "fade";
    renderApp();
  }
}

// ==========================================================================
// 8. Initial Initialization
// ==========================================================================

document.addEventListener("DOMContentLoaded", () => {
  loadSavedState();
  renderApp();
});